import { ProAnalysis } from './pro-analysis';
const TacticReport = require('./tactic-report');
const SuccessRate = require('./success-rate');
const Job = require('../../lib/util/job');
const StudyWorker = Job('./deck-stability.js');
const ManaCost = require('./metrics/mana-cost');
const ManaCostVisual = require('./visuals/mana-curve').default;
const ManaSupply = require('./metrics/mana-supply');
const ManaSupplyVisual = require('./visuals/mana-supply').default;
const PruningTips = require('./pruning/index');
const template = require('./analysis.tpl').default;

export default DomClass(template, function Analysis() {
	var _t = this;

	var strategy = { tactics: [], cardCount: 0 };
	var tacticIndex = {};
	var cards = [];
	var deck = null;
	var commander = null;
	var manacost = null;
	var supply = null; // land
	var landPace = '';

	this.__dom.startingHandSize.onchange = function() {
		_t.analyze(strategy, cards);
	};

	var confidence = 0.5;
	this.confidence = new SuccessRate({value: 100 * confidence});

	var reportStudyProgress = function(report) {
		_t.progress = new SuccessRate({value: 100 * report.progress});
	};

	var reportStudyResults = function(report) {
		const mulliganRate = (
			100 * (1 - Math.pow((1 - report.result.rate/100), 2))
		).toPrecision(2)

		_t.compositeRate = new SuccessRate({
			value: String(report.result.rate)
		});
		_t.mulliganRate = new SuccessRate({
			value: mulliganRate
		});

		for (var tactic in report.result.drawStats) {
			tacticIndex[tactic].setKeepRange(
				report.result.drawStats[tactic],
				_t.compositeRate.value/100 - 0.1
			);
		};

		_t.progress = "Done!";
		_t.classList.remove('calculating');
		_t.makeSuggestions({
			report,
			mulliganRate
		});
	};

	this.getCriteria = function() {
		return strategy.tactics.map(function(t) {
			return {
				name: t.name,
				needed: t.quantityNeeded,
				sample: t.turnNeeded * 1 + _t.startingHandSize * 1,
				drawSize: _t.startingHandSize * 1,
			};
		});
	};

	var refreshTimer = null;
	this.analyze = function(_strategy, _cards, _deck) {
		// local object vars (see above)
		strategy = _strategy; 
		cards = _cards;
		deck = _deck;
		commander = deck.cards.commander;

		if (cards.length == 0 || strategy.tactics.length == 0) {
			this.classList.remove('calculating');
			this.classList.add('empty');
			return;
		}

		if (refreshTimer) {
			clearTimeout(refreshTimer);
		} else {
			this.classList.remove('empty');
			this.classList.add('calculating');
			this.progress = new SuccessRate({value: "0"});
		}

		refreshTimer = setTimeout(function() {
			refreshTimer = null;
			_t.refresh();
		}, 1500);
	};

	this.refresh = function() {
		tacticIndex = {};
		this.tactics = strategy.tactics.map(function(t) {
			var tactic = {
				name: t.name,
				matches: t.count,
				quantityNeeded: t.quantityNeeded,
				startingHandSize: _t.startingHandSize,
				turnNeeded: t.turnNeeded,
				population: strategy.cardCount,
				targetRate: 0.85,
				commander
			};
			var t = new TacticReport(tactic);
			tacticIndex[t.name] = t;
			return t;
		});

		var individualCards = [];
		cards.forEach(function(c) {
			for (var i = 0; i < (c.activeQuantity); i++) {
				individualCards.push(c);
			}
		});
		
		var cmcDistribution = this.refreshManaCurve(individualCards);
		this.refreshManaSupplyDemand(individualCards, cmcDistribution);
		this.study(individualCards);
		// this.makeSuggestions();
	};

	this.study = function(individualCards) {
		const population = individualCards.map(c => c.tactics);
		const basePopulation = [];
		if (commander) basePopulation.push(commander.tactics);

		const successCriteria = this.getCriteria();
		(new StudyWorker())
			.run(population, basePopulation, successCriteria, 200, 2, confidence)
			.onprogress(reportStudyProgress)
			.and(reportStudyResults)
		;
	};

	this.refreshManaCurve = function(individualCards) {
		manacost = new ManaCost(individualCards);
		this.manaCurve = new ManaCostVisual({data: manacost});
		return manacost.curve;
	};

	this.refreshManaSupplyDemand = function(individualCards) {
		supply = new ManaSupply(individualCards, _t.startingHandSize);
		this.manaSupply = new ManaSupplyVisual({supply: supply});
		return supply;
	};

	this.makeSuggestions = function({report, mulliganRate}) {
		// this.pruningTips.refresh(cards, this.getCriteria(), deck);
		const materializedSupply = [];
		for (let pct = 0; pct <= 100; pct++) {
			materializedSupply.push(supply.threshold(pct/100));
		}

		this.proAnalysis.refresh?.({
			deck,
			tactics: _t.tactics.map(({name, turnNeeded, quantityNeeded}) => ({
				name,
				turnNeeded: Number(turnNeeded),
				quantityNeeded: Number(quantityNeeded),
				actualQuantity: Number(tacticIndex[name].actualQuantity),
				actualTurn: Number(tacticIndex[name].actualTurn),
				successRate: Number(tacticIndex[name].successRate.value),
				keepLower: Number(tacticIndex[name].keepLower),
				keepUpper: Number(tacticIndex[name].keepUpper),
				needed: Number(quantityNeeded),
				sample: Number(_t.startingHandSize) + Number(turnNeeded),
			})),
			cards: cards.map(c => ({
				name: c.name,
				type: c.type,
				text: c.text,
				cmc: c.cmc,
				manaCost: c.manaCost,
				rarity: c.rarity,
				power: c.power,
				toughness: c.toughness,
				activeQuantity: Number(c.activeQuantity),
				sideboarded: Number(c.sideboarded),
				tactics: c.tactics
			})),
			commander: commander ? ({
				name: commander.name,
				activeQuantity: Number(commander.activeQuantity),
				sideboarded: Number(commander.sideboarded),
				text: commander.text,
				tactics: commander.tactics
			}) : undefined,
			// report,
			successRate: Number(report.result.rate),
			mulliganRate,
			missRate: Number(report.result.missRate),

			// these are coming through as [object object] ... 
			manacost,
			supply: materializedSupply,
		});
	};

	this.lock = function() {
		this.classList.add('locked');
	};

	this.unclock = function() {
		this.classList.remove('locked');
	};

	this.init = function() {
		this.proAnalysis = ProAnalysis();
	};
});
